"use client";

import {
  useEffect,
  useState,
  useCallback,
  useOptimistic,
  startTransition,
} from "react";

import { Reactions } from "./reactions";
import BlogPostReactions from "./emoji-reaction";
import { Reaction } from "@/lib/types";
import { updateReaction } from "@/lib/actions/blog";
import { toast } from "sonner";

type PendingReaction = {
  id: string;
  title: string;
  metadata: { reaction: string; resource: string };
  // Track if this is user's own reaction
  isUserOwned?: boolean;
};

// Generate a persistent pseudonymous user ID for tracking reactions
const getUserId = () => {
  if (typeof window === "undefined") return null;

  let userId = localStorage.getItem("reaction_user_id");
  if (!userId) {
    userId = `user_${Math.random().toString(36).substring(2, 9)}_${Date.now().toString(36)}`;
    localStorage.setItem("reaction_user_id", userId);
  }
  return userId;
};

// Client component wrapper for reactions to avoid hydration issues
export default function ReactionsWrapper({
  reactions: initialReactions,
  post,
}) {
  const [mounted, setMounted] = useState(false);
  const [reactions, setReactions] = useState<Reaction[]>(initialReactions);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // User-specific reaction tracking
  const [userReactions, setUserReactions] = useState<Record<string, boolean>>(
    {},
  );
  const [throttle, setThrottle] = useState(false);

  // Load user's reaction history from localStorage
  useEffect(() => {
    if (typeof window !== "undefined") {
      // Get the user's reactions for this post
      const key = `post_reactions_${post.id}`;
      const savedReactions = localStorage.getItem(key);

      if (savedReactions) {
        try {
          setUserReactions(JSON.parse(savedReactions));
        } catch (e) {
          console.error("Failed to parse saved reactions", e);
          // Reset if corrupted
          localStorage.removeItem(key);
        }
      }
    }
  }, [post.id]);

  // Save user's reactions to localStorage whenever they change
  useEffect(() => {
    if (mounted && Object.keys(userReactions).length > 0) {
      localStorage.setItem(
        `post_reactions_${post.id}`,
        JSON.stringify(userReactions),
      );
    }
  }, [userReactions, post.id, mounted]);

  // Use the useOptimistic hook for handling optimistic updates
  const [optimisticReactions, addOptimisticReaction] = useOptimistic(
    reactions,
    (
      currentReactions,
      update: {
        reaction: PendingReaction;
        action: "add" | "remove";
        isUserAction: boolean;
      },
    ) => {
      const { reaction, action, isUserAction } = update;

      if (action === "remove") {
        // Only remove ONE instance of this reaction (to handle duplicates)
        let foundOne = false;
        return currentReactions.filter((r) => {
          if (
            !foundOne &&
            r.metadata?.reaction === reaction.metadata.reaction &&
            r.title === reaction.title
          ) {
            foundOne = true;
            return false; // Filter out just this one
          }
          return true; // Keep all others
        });
      } else {
        // action === 'add'
        // Add a new reaction
        return [...currentReactions, reaction as Reaction];
      }
    },
  );

  // Function to refresh reactions from the server - only when page loads
  const refreshReactions = useCallback(
    async (showLoading = true) => {
      if (isRefreshing) return; // Prevent concurrent refreshes

      try {
        if (showLoading) setIsRefreshing(true);

        // Get the user ID if available
        const userId = getUserId();

        // Include userId in the request to identify user-owned reactions
        const url = userId
          ? `/api/reactions?postId=${post.id}&userId=${encodeURIComponent(userId)}`
          : `/api/reactions?postId=${post.id}`;

        const response = await fetch(url);
        const data = await response.json();

        // Even if the API returns an error, it should include an empty objects array
        const objects = data.objects || [];

        // Update reactions without causing flickering
        setReactions(objects);

        // Also update userReactions state based on isUserOwned flag from the server
        if (userId) {
          const newUserReactions = {};
          objects.forEach((reaction) => {
            if (reaction.isUserOwned) {
              const key = `${reaction.metadata.reaction}-${reaction.title}`;
              newUserReactions[key] = true;
            }
          });

          // Only update if there are actual user reactions
          if (Object.keys(newUserReactions).length > 0) {
            setUserReactions((prev) => ({ ...prev, ...newUserReactions }));
          }
        }
      } catch (err) {
        console.error("Error refreshing reactions:", err);
      } finally {
        if (showLoading) setIsRefreshing(false);
      }
    },
    [post.id, isRefreshing],
  );

  useEffect(() => {
    setMounted(true);

    // Only fetch reactions once when the component mounts
    refreshReactions(false);

    // No interval polling - only refresh when the page loads
  }, [refreshReactions]);

  // Check if the current user has already reacted with this emoji
  const hasUserReacted = useCallback(
    (emoji: string, title: string) => {
      const reactionKey = `${emoji}-${title}`;
      return !!userReactions[reactionKey];
    },
    [userReactions],
  );

  // Check if a specific reaction type exists at all (from any user)
  const reactionExists = useCallback(
    (emoji: string, title: string) => {
      return optimisticReactions.some(
        (r) => r.metadata?.reaction === emoji && r.title === title,
      );
    },
    [optimisticReactions],
  );

  // Function to handle adding or removing a reaction (optimistic update)
  const handleReactionToggle = useCallback(
    async (emoji: string, title: string) => {
      // Create a unique reaction key for tracking
      const reactionKey = `${emoji}-${title}`;
      console.log(
        `Toggle reaction: ${reactionKey}, Current state:`,
        userReactions,
      );

      // Prevent rapid clicks during throttle period
      if (throttle) {
        return;
      }

      // Set throttle to prevent spam clicks
      setThrottle(true);
      setTimeout(() => setThrottle(false), 1000);

      // Check if this user has already reacted with this emoji
      const userHasReacted = hasUserReacted(emoji, title);
      console.log(`User has already reacted: ${userHasReacted}`);

      const reactionExists = optimisticReactions.some(
        (r) => r.metadata?.reaction === emoji && r.title === title,
      );
      console.log(`Reaction exists (from any user): ${reactionExists}`);

      // Only remove if the user has already reacted with this emoji
      // Otherwise, always add a new reaction
      const action = userHasReacted ? "remove" : "add";
      console.log(`Action to take: ${action}`);

      // Create a unique reaction object
      const uniqueId = `${emoji}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const optimisticReaction: PendingReaction = {
        id: uniqueId,
        title: title,
        metadata: { reaction: emoji, resource: post.id },
        isUserOwned: true,
      };

      // Generate a persistent user ID
      const userId = getUserId();
      console.log(`Current userId: ${userId}`);

      // Wrap both the optimistic update and the server action in startTransition
      startTransition(async () => {
        // First, apply the optimistic update
        addOptimisticReaction({
          reaction: optimisticReaction,
          action,
          isUserAction: true,
        });

        // Update user reaction tracking immediately for UI purposes
        // This also gets persisted to localStorage via the useEffect
        setUserReactions((prev) => {
          const newState = {
            ...prev,
            [reactionKey]: !userHasReacted, // Toggle the state
          };
          console.log(`Updated userReactions:`, newState);
          return newState;
        });

        try {
          // Then call the server action to update the reaction
          // Include the userId for ownership tracking
          console.log(
            `Calling server action with: ${post.id}, ${emoji}, ${title}, ${userId}`,
          );
          const result = await updateReaction(post.id, emoji, title, userId);
          console.log(`Server action result:`, result);

          if (result.error) {
            // Show error toast
            toast.error(`Error updating reaction: ${result.error}`);
            console.error("Error updating reaction:", result.error);
          } else if (result.toggled) {
            // Show success toast based on the action
            const actionText = result.removed ? "removed" : "added";
            toast.success(`Your ${emoji} reaction has been ${actionText}`);
          }

          // Always refresh reactions after server update to ensure sync
          refreshReactions(false);
        } catch (error) {
          console.error("Error in reaction toggle:", error);
          toast.error("Something went wrong. Please try again.");
        }
      });
    },
    [
      post.id,
      addOptimisticReaction,
      hasUserReacted,
      optimisticReactions,
      refreshReactions,
      throttle,
      userReactions,
    ],
  );

  if (!mounted) {
    return (
      <div className="flex w-full items-center justify-between pt-1">
        <div className="flex h-full w-full items-center gap-x-1 py-1 sm:justify-end">
          {/* Placeholder while hydrating */}
          <div className="h-8 w-16 animate-pulse rounded-full bg-stone-100 dark:bg-stone-800"></div>
        </div>
        <div className="h-10 w-10"></div>
      </div>
    );
  }

  // Calculate counts for each emoji
  const emojiCounts = optimisticReactions.reduce(
    (acc, reaction: Reaction) => {
      const title = reaction.title;
      const emoji = reaction.metadata?.reaction;
      if (!acc[title]) {
        acc[title] = { emoji, count: 0 };
      }
      acc[title].count++;
      return acc;
    },
    {} as Record<string, { emoji: string; count: number }>,
  );

  return (
    <div className="flex w-full items-center justify-between pt-1">
      <div className="flex h-full w-full items-center gap-x-1 overflow-x-auto py-1 sm:justify-end">
        <div className="flex w-full items-center justify-start gap-x-1 sm:justify-end">
          {Object.entries(emojiCounts)
            .reverse()
            .map(([title, { emoji, count }]) => (
              <Reactions
                key={title}
                title={title}
                count={count}
                emoji={emoji}
                id={post.id}
                onReactionAdded={() => handleReactionToggle(emoji, title)}
                isActive={hasUserReacted(emoji, title)}
              />
            ))}
        </div>
      </div>
      <BlogPostReactions
        id={post.id}
        slug={post.slug}
        onEmojiSelect={(emoji) =>
          handleReactionToggle(
            emoji.native,
            emoji.native === "👍"
              ? "Thumbs Up"
              : emoji.native === "👌"
                ? "Ok Hand"
                : emoji.name,
          )
        }
      />
    </div>
  );
}
